import React from 'react';

import { Badge, Box, BoxProps, Button, ButtonProps, Flex, ScrollArea, Space, Title } from '@mantine/core';
import { IconBrandFacebook } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError, HttpStatusCode } from 'axios';
import { ErrorAlert } from 'components/ErrorAlert';
import { FacebookAccountCard } from 'components/FacebookAccountHandler/FacebookAccountCard';
import { FullScreenLoader } from 'components/FullScreenLoader';
import { NoDataFound } from 'components/NoDataFound';
import { UpgradePlanBlurCard } from 'components/UpgradePlanBlurCard';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { config } from 'environment/config';
import { FacebookIcon } from 'icons/FacebookIcon';
import { useTranslation } from 'react-i18next';
import { FacebookService } from 'services/facebook-service';
import { NotificationService } from 'services/notification-service';
import { FACEBOOK_ACCOUNTS_QUERY_KEY } from 'shared/constants/query-keys';
import { FacebookAccount } from 'shared/interfaces/FacebookAccount';
import { FacebookLoginResponse } from 'shared/interfaces/FacebookSDK';

type FacebookAccountHandlerProps = BoxProps;

export function FacebookAccountHandler({ ...boxProps }: FacebookAccountHandlerProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [subscription] = useSubscriptionContext();

  const facebookAccountsQuery = useQuery({
    queryKey: [FACEBOOK_ACCOUNTS_QUERY_KEY],
    queryFn: FacebookService.getAllAccounts,
    enabled: !!subscription.product.enableFacebook
  });

  const connectFacebookAccountsMutation = useMutation({
    mutationFn: FacebookService.connectAccounts,
    onSuccess: (accounts: FacebookAccount[]) => {
      queryClient.setQueryData([FACEBOOK_ACCOUNTS_QUERY_KEY], accounts);
      NotificationService.showResult(true, 'FEED_BACK.FACEBOOK.SUCCESS.CONNECT');
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === HttpStatusCode.Conflict) {
        NotificationService.showResult(false, 'FEED_BACK.FACEBOOK.FAILURE.CONFLICT');
      } else {
        NotificationService.showResult(false, 'FEED_BACK.FACEBOOK.FAILURE.CONNECT');
      }
    }
  });

  const launchFacebookSignup = () => {
    if (!window.FB) return;
    window.FB.login(
      (response: FacebookLoginResponse) => {
        if (response.authResponse?.code) {
          connectFacebookAccountsMutation.mutate({
            code: response.authResponse.code
          });
        } else {
          NotificationService.showResult(false, 'FEED_BACK.FACEBOOK.FAILURE.CONNECT');
        }
      },
      {
        config_id: config.facebookEmbeddedSignupConfigId,
        response_type: 'code',
        override_default_response_type: true
      }
    );
  };

  if (facebookAccountsQuery.isError) return <ErrorAlert h={boxProps.h} />;
  if (facebookAccountsQuery.isInitialLoading) return <FullScreenLoader h={boxProps.h} />;
  const facebookAccounts = facebookAccountsQuery.data || [];

  const CreateButton = ({ ...buttonProps }: ButtonProps) => (
    <Button
      variant="default"
      leftIcon={<FacebookIcon w={18} h={18} />}
      loading={connectFacebookAccountsMutation.isLoading}
      onClick={launchFacebookSignup}
      {...buttonProps}>
      {t('FACEBOOK.CONNECT_FACEBOOK_ACCOUNT')}
    </Button>
  );

  return (
    <UpgradePlanBlurCard message={t('FACEBOOK.UPGRADE.DESCRIPTION')} showCard={!subscription.product.enableFacebook}>
      <Box {...boxProps}>
        <Flex justify="space-between" align="center">
          <Flex direction="row" align="center">
            <Title order={5}>{t('FACEBOOK.CONNECTED_ACCOUNT')}</Title>
            <Space w={10} />
            <Badge px={4} mt={3} size="xs" color="gray">
              {facebookAccounts.length}
            </Badge>
          </Flex>
          {facebookAccounts.length > 0 && <CreateButton variant="default" />}
        </Flex>
        <ScrollArea w="100%" h="100%" mt={10}>
          {facebookAccounts.map((account, idx) => (
            <FacebookAccountCard
              key={idx}
              account={account}
              mt={idx === 0 ? 0 : 5}
              mb={idx === facebookAccounts.length - 1 ? 0 : 5}
              onRemove={launchFacebookSignup}
            />
          ))}
          {facebookAccounts.length == 0 && subscription.product.enableFacebook && (
            <NoDataFound
              h="100%"
              size="xs"
              icon={<IconBrandFacebook />}
              title={t('NO_DATA_FOUND.FACEBOOK.TITLE')}
              subTitle={t('NO_DATA_FOUND.FACEBOOK.SUB_TITLE')}
              button={<CreateButton size="xs" />}
            />
          )}
        </ScrollArea>
      </Box>
    </UpgradePlanBlurCard>
  );
}
